import { Injectable, OnDestroy } from "@angular/core";
import { UserService } from "app/core/user/user.service";
import { Subject, takeUntil } from "rxjs";

export type ChangelogModalAction = "cta" | "skip" | "close";

interface BaseProps {
    releaseId?: string;
    entryId?: string;
    deepLink?: string;
    action?: ChangelogModalAction;
    [key: string]: unknown;
}

interface EnrichedEvent extends BaseProps {
    event: string;
    timestamp: string;
    userId?: string;
    userRole?: string;
}

/**
 * Swappable sink for changelog telemetry events.
 *
 * Today the sink just logs structured JSON to the browser console — enough to
 * verify events fire during development and to inspect flow in DevTools.
 * When the team picks a provider (GA4, PostHog, custom endpoint...), swap
 * the `send()` implementation; every call-site stays the same.
 */
@Injectable({ providedIn: "root" })
export class ChangelogTelemetryService implements OnDestroy {
    private _userId?: string;
    private _userRole?: string;
    private _unsubscribeAll = new Subject<void>();

    constructor(private _userService: UserService) {
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user) => {
            this._userId = user?.id;
            this._userRole = user?.role;
        });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    trackChangelogOpened(props: BaseProps = {}): void {
        this._send("changelog_opened", props);
    }

    trackEntryViewed(props: { releaseId: string; entryId: string } & BaseProps): void {
        this._send("entry_viewed", props);
    }

    trackDeepLinkClicked(
        props: { releaseId: string; entryId: string; deepLink: string } & BaseProps
    ): void {
        this._send("deeplink_clicked", props);
    }

    trackModalShown(props: { releaseId: string } & BaseProps): void {
        this._send("modal_shown", props);
    }

    trackModalDismissed(
        props: { releaseId: string; action: ChangelogModalAction } & BaseProps
    ): void {
        this._send("modal_dismissed", props);
    }

    /**
     * Central sink. Replace this with an external provider when ready.
     * Keep the event name + payload shape stable — call-sites depend on it.
     */
    private _send(event: string, props: BaseProps): void {
        const payload: EnrichedEvent = {
            event,
            timestamp: new Date().toISOString(),
            userId: this._userId,
            userRole: this._userRole,
            ...props,
        };
        // eslint-disable-next-line no-console
        console.info("[changelog-telemetry]", payload);
    }
}
